import {ChangeDetectionStrategy, Component, inject, Inject, signal, ViewChild} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef} from '@angular/material/dialog';
import {Subscription} from 'rxjs';
import {Appointment, AppointmentType, CalendarAppointment, calendarAppointmentToWriteModel, formatPatientName, formatZoneAbbrev, Permissions, practiceLocalToInstant, PracticeTimezoneService, zonesDifferAt} from '@xmedical/core';
import {MatButtonModule} from '@angular/material/button';
import {MatIconModule} from '@angular/material/icon';
import {MatTooltipModule} from '@angular/material/tooltip';
import {AppointmentEditFormComponent} from '../appointment-edit-form/appointment-edit-form.component';
import {AppointmentNotificationLogComponent} from '../appointment-notification-log/appointment-notification-log.component';
import {ConfirmationDialogComponent} from '@xmedical/ui';
import {XmedicalUiModule} from '@xmedical/ui';
import {CALENDAR_ACTION_HANDLER, CALENDAR_DATA_PROVIDER} from '../../tokens/calendar.tokens';
import dayjs from 'dayjs/esm';
import utc from 'dayjs/esm/plugin/utc';
import timezone from 'dayjs/esm/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

export interface AppointmentDetailDialogData {
  appointmentTypes: AppointmentType[];
  appointment: CalendarAppointment;
  /** Whether the calendar is filtering by appointment type (forwarded to the clash banner). */
  appointmentTypeFiltersActive?: boolean;
}

@Component({
  selector: 'app-appointment-detail-dialog',
  templateUrl: './appointment-detail-dialog.component.html',
  styleUrls: ['./appointment-detail-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    XmedicalUiModule,
    AppointmentEditFormComponent,
  ]
})
export class AppointmentDetailDialogComponent {
  @ViewChild(AppointmentEditFormComponent) editForm: AppointmentEditFormComponent;

  mode = signal<'detail' | 'edit'>('detail');

  // Exposed for the template's *xuiPermissionRequired bindings — new checks use the
  // generated Permissions enum (AGENTS.md) so a rename/typo fails at compile time.
  readonly Permissions = Permissions;

  private dialogSubscription: Subscription;
  private deleteSubscription: Subscription;

  private dataProvider = inject(CALENDAR_DATA_PROVIDER);
  private actionHandler = inject(CALENDAR_ACTION_HANDLER);
  private practiceTz = inject(PracticeTimezoneService);

  constructor(
    private dialog: MatDialog,
    public dialogRef: MatDialogRef<AppointmentDetailDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: AppointmentDetailDialogData,
  ) {}

  get appointment(): CalendarAppointment {
    return this.data.appointment;
  }

  get hasPatient(): boolean {
    return this.appointment.patient != null;
  }

  get isPatientAppointment(): boolean {
    return this.appointment.appointmentType?.isPatientAppointment;
  }

  get headerColor(): string {
    return this.appointment.appointmentType?.displayStyle || '#1976d2';
  }

  get displayName(): string {
    if (this.appointment.patient) {
      const format = this.dataProvider.practiceSettings()?.defaultPatientDisplayFormat;
      return formatPatientName(this.appointment.patient, format);
    }
    return this.appointment.title || this.appointment.metaData?.name || 'Appointment';
  }

  get formattedDateTime(): string {
    const start = this.appointment.start;
    if (!start) return '';
    const zone = this.practiceTz.practiceTimezone();
    const device = this.practiceTz.deviceTimezone;

    // start/end are zone-less practice-local; resolve to the true instant, then
    // render in the practice zone.
    const startInstant = practiceLocalToInstant(start, zone);
    const s = dayjs(startInstant).tz(zone);
    const dayName = s.format('dddd');
    const date = s.format('D MMMM');
    const startTime = s.format('HH:mm');
    const endInstant = this.appointment.end ? practiceLocalToInstant(this.appointment.end, zone) : null;
    const endTime = endInstant ? dayjs(endInstant).tz(zone).format('HH:mm') : null;

    let line = endTime
      ? `${dayName}, ${date}  ·  ${startTime} - ${endTime}`
      : `${dayName}, ${date}  ·  ${startTime}`;

    // When the viewer is out of zone, label the practice time and append their
    // own local start time so there is no "whose clock?" ambiguity.
    if (zonesDifferAt(startInstant, zone, device)) {
      const deviceStart = dayjs(startInstant).tz(device).format('HH:mm');
      line += `  ${formatZoneAbbrev(zone, startInstant)} · ${deviceStart} your time`;
    }
    return line;
  }

  get cellNumber(): string | null {
    return this.appointment.patient?.cell || this.appointment.metaData?.cell || null;
  }

  get email(): string | null {
    return this.appointment.patient?.email || this.appointment.metaData?.email || null;
  }

  switchToEdit(): void {
    this.mode.set('edit');
  }

  switchToDetail(): void {
    this.mode.set('detail');
  }

  onSaved(appointment: CalendarAppointment): void {
    this.dialogRef.close(appointment);
  }

  triggerSave(): void {
    this.editForm.save();
  }

  close(): void {
    this.dialogRef.close();
  }

  sendSingleMessage(): void {
    this.actionHandler.onSendMessageRequested?.(this.appointment.patient);
  }

  sendSingleEmail(): void {
    this.actionHandler.onSendEmailRequested?.(this.appointment.patient);
  }

  removeAppointment(): void {
    const confirmRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {title: 'Remove Appointment', message: 'Are you sure you want to remove this appointment?'}
    });
    this.dialogSubscription = confirmRef.afterClosed().subscribe(result => {
      if (result) {
        this.deleteSubscription = this.dataProvider.deleteAppointment(this.appointment.id).subscribe(() => {
          this.dialogRef.close('deleted');
        });
      }
    });
  }

  /** The log is only reachable where the provider can fetch it (practice-scoped apps);
   *  the organisation-portal omits loadAppointmentNotifications, so hide the button there. */
  get canViewNotificationLog(): boolean {
    return !!this.appointment.id && typeof this.dataProvider.loadAppointmentNotifications === 'function';
  }

  viewNotificationLog(): void {
    this.dialog.open(AppointmentNotificationLogComponent, {
      width: '800px',
      data: {appointmentId: this.appointment.id}
    });
  }

  viewPatient(): void {
    this.actionHandler.onViewPatientRequested(this.appointment.patient);
    this.dialogRef.close();
  }

  viewPatientClinical(): void {
    this.actionHandler.onViewPatientClinicalRequested?.(this.appointment.patient);
    this.dialogRef.close();
  }

  get canViewClinical(): boolean {
    return typeof this.actionHandler.onViewPatientClinicalRequested === 'function';
  }

  createPatient(): void {
    this.dialogSubscription = this.actionHandler.onCreatePatientRequested(this.appointment).subscribe(patient => {
      if (patient) {
        const updated = calendarAppointmentToWriteModel(this.appointment, {patientId: patient.id});
        this.dataProvider.updateAppointment(updated).subscribe(() => {
          this.dialogRef.close('updated');
        });
      }
    });
  }
}
